import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import { useToast } from '../hooks/useToast';
import getCSRFtoken from '../utils/getCSRFtoken';
import fetchWithAuth from '../utils/fetchWithAuth';

function GenerateTrancheModal({ isOpen, onClose, poll, audience, onSuccess, isFirstTranche = false }) {
  const [trancheSize, setTrancheSize] = useState(poll?.default_tranche_size || 100);
  const [generating, setGenerating] = useState(false);
  const [confirmLarge, setConfirmLarge] = useState(false);
  const [smsSegments, setSmsSegments] = useState(1);
  const [variantLock, setVariantLock] = useState('');

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    if (isOpen) {
      setConfirmLarge(false);
      fetchPreflightData();
    }
  }, [isOpen]);

  const fetchPreflightData = async () => {
    if (!poll?.id) return;
    try {
      const response = await fetchWithAuth(`/api/polls/${poll.id}/preflight/`);
      if (response.ok) {
        const data = await response.json();
        if (data.sms_preview) {
          setSmsSegments(data.sms_preview.segments || 1);
        }
      }
    } catch (err) {
      console.error('Failed to fetch preflight data:', err);
    }
  };

  const estimatedCost = (trancheSize * smsSegments * 0.0079).toFixed(2);

  const handleGenerate = async () => {
    // Large tranche confirmation
    if (trancheSize > 500 && !confirmLarge) {
      setConfirmLarge(true);
      return;
    }

    setGenerating(true);

    try {
      const response = await fetchWithAuth('/api/next_tranche/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFtoken(),
        },
        body: JSON.stringify({
          poll_id: poll.id,
          audience_id: audience.id,
          tranche_size: trancheSize,
          variant_lock: variantLock || null,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate tranche';
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            errorMessage = `Server error (${response.status})`;
          }
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.oversampled) {
        toast.showSuccess(
          'Tranche Created',
          `Selected ${data.respondent_count} to target ~${data.requested_size} reachable (${Math.round((data.phone_match_rate || 0.55) * 100)}% match rate). ${data.ready_to_send} ready now.`
        );
      } else {
        toast.showSuccess(
          'Tranche Created',
          `${data.respondent_count} respondents ready (${data.ready_to_send || data.respondent_count} sendable)`
        );
      }

      if (onSuccess) {
        onSuccess(data);
      }

      onClose();
    } catch (error) {
      console.error('Error generating tranche:', error);
      toast.showError('Error', error.message || 'Failed to generate tranche');
    } finally {
      setGenerating(false);
      setConfirmLarge(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Generate Next Tranche</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm">
                Selects individuals and prepares them for SMS sending. No messages are sent until
                you manually send each one via the Send Queue.
              </Text>
            </Alert>

            {/* First tranche warning */}
            {isFirstTranche && trancheSize > 100 && (
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">
                  This is the first tranche. Consider starting with 50-100 to verify everything works before scaling up.
                </Text>
              </Alert>
            )}

            <FormControl>
              <FormLabel>Tranche Size</FormLabel>
              <NumberInput
                value={trancheSize}
                onChange={(valueString) => {
                  setTrancheSize(parseInt(valueString) || 0);
                  setConfirmLarge(false);
                }}
                min={1}
                max={10000}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <HStack mt={1} spacing={4}>
                <Text fontSize="sm" color="gray.600">
                  {trancheSize} message{trancheSize !== 1 ? 's' : ''}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Est. cost: ${estimatedCost}
                </Text>
              </HStack>
            </FormControl>

            {poll?.invitation_variants && poll.invitation_variants.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm">Invitation Variant</FormLabel>
                <Select
                  size="sm"
                  value={variantLock}
                  onChange={e => setVariantLock(e.target.value)}
                >
                  <option value="">Random Split (equal)</option>
                  {poll.invitation_variants.map(v => (
                    <option key={v.label} value={v.label}>
                      Variant {v.label} only — {(v.text || '').substring(0, 50)}...
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {poll?.target_demographics && (
              <Alert status="success" size="sm">
                <AlertIcon />
                <VStack align="start" spacing={0}>
                  <Text fontSize="sm" fontWeight="bold">
                    Target demographics configured
                  </Text>
                  <Text fontSize="xs">
                    Sampling will prioritize underrepresented groups
                  </Text>
                </VStack>
              </Alert>
            )}

            {/* Large tranche confirmation */}
            {confirmLarge && (
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">
                  You are about to create <strong>{trancheSize}</strong> respondents
                  (est. ${estimatedCost} if all sent). Click "Confirm" to proceed.
                </Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={generating}>
            Cancel
          </Button>
          <Button
            colorScheme={confirmLarge ? 'orange' : 'blue'}
            onClick={handleGenerate}
            isLoading={generating}
            loadingText="Generating..."
          >
            {confirmLarge ? 'Confirm' : 'Generate Tranche'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default GenerateTrancheModal;
